import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { fetchMCPTools, callMCPTool, type MCPTool, type MCPCallResponse } from '../lib/api'

interface ToolTesterProps {
  endpointUrl: string;
  protocol: string;
  onClose: () => void;
  initialToolName?: string;
  paymentStatus?: Record<string, boolean | null>;  // tool name → true=paid, false=free, null=unknown
}

export function ToolTester({ endpointUrl, protocol, onClose, initialToolName, paymentStatus }: ToolTesterProps) {
  const [tools, setTools] = useState<MCPTool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedTool, setSelectedTool] = useState<MCPTool | null>(null)
  const [args, setArgs] = useState<Record<string, string>>({})
  const [calling, setCalling] = useState(false)
  const [result, setResult] = useState<MCPCallResponse | null>(null)

  useEffect(() => {
    if (protocol !== 'mcp') {
      setError('Tool testing is only available for MCP endpoints')
      setLoading(false)
      return
    }

    fetchMCPTools(endpointUrl)
      .then(res => {
        if (res.success && res.tools.length > 0) {
          setTools(res.tools)
          // Auto-select the initial tool if specified, otherwise first tool
          const initialTool = initialToolName
            ? res.tools.find(t => t.name === initialToolName)
            : null
          setSelectedTool(initialTool || res.tools[0] || null)
        } else if (res.error) {
          setError(res.error)
        } else {
          setError('No tools found at this endpoint')
        }
      })
      .catch(err => {
        setError(err.message || 'Failed to fetch tools')
      })
      .finally(() => setLoading(false))
  }, [endpointUrl, protocol, initialToolName])

  // Reset args when tool changes
  useEffect(() => {
    setArgs({})
    setResult(null)
  }, [selectedTool])

  const handleCall = async () => {
    if (!selectedTool) return

    setCalling(true)
    setResult(null)

    try {
      // Convert string args to proper types based on schema
      const typedArgs: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(args)) {
        const propSchema = selectedTool.inputSchema?.properties?.[key]
        if (propSchema?.type === 'number' || propSchema?.type === 'integer') {
          typedArgs[key] = Number(value)
        } else if (propSchema?.type === 'boolean') {
          typedArgs[key] = value === 'true'
        } else {
          typedArgs[key] = value
        }
      }

      const res = await callMCPTool(endpointUrl, selectedTool.name, typedArgs)
      setResult(res)
    } catch (err) {
      setResult({
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setCalling(false)
    }
  }

  const properties = selectedTool?.inputSchema?.properties || {}
  const required = selectedTool?.inputSchema?.required || []

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-2xl max-h-[85vh] bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-[var(--border-subtle)] flex items-center justify-between flex-shrink-0">
            <div>
              <h2 className="text-lg font-display font-medium text-[var(--text-primary)]">
                MCP Tool Tester
              </h2>
              <p className="text-xs font-mono text-[var(--text-muted)] mt-0.5 truncate max-w-md">
                {endpointUrl}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              <XIcon className="w-5 h-5 text-[var(--text-muted)]" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-[var(--text-muted)]">
                  <LoadingSpinner />
                  <span className="font-mono text-sm">Fetching tools...</span>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-[var(--text-muted)] font-mono text-sm">{error}</div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Tool Selector */}
                <div>
                  <label className="block text-xs font-mono text-[var(--text-muted)] mb-2">
                    Select Tool ({tools.length} available)
                  </label>
                  <select
                    value={selectedTool?.name || ''}
                    onChange={(e) => {
                      const tool = tools.find(t => t.name === e.target.value)
                      setSelectedTool(tool || null)
                    }}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--bg-tertiary)] border border-[var(--border-default)] text-[var(--text-primary)] font-mono text-sm focus:outline-none focus:border-[var(--accent-teal)]"
                  >
                    {tools.map(tool => {
                      const status = paymentStatus?.[tool.name]
                      const tag = status === true ? ' [$]' : status === false ? ' [FREE]' : ''
                      return (
                        <option key={tool.name} value={tool.name}>
                          {tool.name}{tag}
                        </option>
                      )
                    })}
                  </select>
                  {selectedTool && (
                    <div className="mt-2 flex items-start gap-2">
                      {paymentStatus && (() => {
                        const status = paymentStatus[selectedTool.name]
                        if (status === false) return (
                          <span className="shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-400/20 text-emerald-400">
                            FREE
                          </span>
                        )
                        if (status === true) return (
                          <span className="shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-400/20 text-amber-400">
                            PAID
                          </span>
                        )
                        return (
                          <span className="shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
                            ?
                          </span>
                        )
                      })()}
                      {selectedTool.description && (
                        <p className="text-sm text-[var(--text-secondary)]">
                          {selectedTool.description}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Arguments */}
                {Object.keys(properties).length > 0 && (
                  <div>
                    <label className="block text-xs font-mono text-[var(--text-muted)] mb-2">
                      Arguments
                    </label>
                    <div className="space-y-3">
                      {Object.entries(properties).map(([key, prop]) => (
                        <div key={key}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-mono text-[var(--text-primary)]">
                              {key}
                            </span>
                            <span className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
                              {prop.type}
                            </span>
                            {required.includes(key) && (
                              <span className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]">
                                required
                              </span>
                            )}
                          </div>
                          {prop.description && (
                            <p className="text-xs text-[var(--text-muted)] mb-1">
                              {prop.description}
                            </p>
                          )}
                          <input
                            type={prop.type === 'number' || prop.type === 'integer' ? 'number' : 'text'}
                            value={args[key] || ''}
                            onChange={(e) => setArgs(prev => ({ ...prev, [key]: e.target.value }))}
                            placeholder={`Enter ${key}...`}
                            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-default)] text-[var(--text-primary)] font-mono text-sm placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-teal)]"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {Object.keys(properties).length === 0 && selectedTool && (
                  <div className="text-sm text-[var(--text-muted)] font-mono">
                    This tool takes no arguments.
                  </div>
                )}

                {/* Result */}
                {result && (
                  <div>
                    <label className="block text-xs font-mono text-[var(--text-muted)] mb-2">
                      Result
                      {result.latency_ms && (
                        <span className="ml-2 text-[var(--accent-teal)]">
                          ({result.latency_ms}ms)
                        </span>
                      )}
                    </label>
                    <div className={`p-4 rounded-xl border ${
                      result.success
                        ? 'bg-[var(--status-online)]/5 border-[var(--status-online)]/20'
                        : result.payment_required
                        ? 'bg-[var(--accent-gold)]/5 border-[var(--accent-gold)]/20'
                        : 'bg-red-500/5 border-red-500/20'
                    }`}>
                      {result.payment_required ? (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[var(--accent-gold)] font-mono text-sm font-medium">
                              💰 Payment Required
                            </span>
                          </div>
                          <pre className="text-xs font-mono text-[var(--text-secondary)] overflow-x-auto whitespace-pre-wrap">
                            {JSON.stringify(result.payment_info, null, 2)}
                          </pre>
                        </div>
                      ) : result.auth_required ? (
                        <div className="text-[var(--accent-purple)] font-mono text-sm">
                          🔐 Authentication required (HTTP {result.status})
                        </div>
                      ) : result.success ? (
                        <pre className="text-xs font-mono text-[var(--text-primary)] overflow-x-auto whitespace-pre-wrap">
                          {typeof result.result === 'string'
                            ? result.result
                            : JSON.stringify(result.result, null, 2)}
                        </pre>
                      ) : (
                        <div>
                          <div className="text-red-400 font-mono text-sm mb-2">Error</div>
                          <pre className="text-xs font-mono text-[var(--text-secondary)] overflow-x-auto whitespace-pre-wrap">
                            {typeof result.error === 'string'
                              ? result.error
                              : JSON.stringify(result.error, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {!loading && !error && selectedTool && (
            <div className="px-6 py-4 border-t border-[var(--border-subtle)] flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-[var(--border-default)] text-[var(--text-secondary)] font-mono text-sm hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleCall}
                disabled={calling}
                className="px-6 py-2 rounded-xl bg-[var(--accent-teal)] text-[var(--bg-primary)] font-mono text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
              >
                {calling ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Calling...
                  </>
                ) : (
                  <>
                    <PlayIcon className="w-4 h-4" />
                    Call Tool
                  </>
                )}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// Icons
function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'
  return (
    <svg className={`${sizeClass} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}
